/**
 * useCounterBiasWhisper — polling + dismiss wrapper around the pure
 * `detectCounterBiasSetup` detector.
 *
 * Why this hook exists (pain point, 2026-05-18): woke up bullish, watched
 * for a long off the 15M 21EMA bounce, missed a textbook clean Golden
 * Crayon rejection short on the 4H/15M. The bias was actively filtering
 * out the real setup.
 *
 * What this hook does:
 *   - Reads the trader's session thesis from the journal entry.
 *   - Polls the most recent 5m bars on the active symbol every 60s.
 *   - Runs the pure detector against every fresh poll.
 *   - Persists dismissed whispers per-bar (so dismissing doesn't re-fire
 *     on the very next poll).
 *   - Returns at most ONE active whisper at a time. New bar + new pattern
 *     → new whisper key → eligible to re-surface even if a prior whisper
 *     was dismissed.
 *
 * What this hook DOES NOT do:
 *   - Doesn't decide where to render (caller picks the surface).
 *   - Doesn't fetch HTF bars beyond 5m (the detector only needs 5m).
 *   - Doesn't speak to the LLM. Static, deterministic, free-tier safe.
 *   - Doesn't run when there's no symbol, no thesis, or we're not on
 *     the current trading day (historical days have no live whisper).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { barDataService } from '../services/barData';
import { detectCounterBiasSetup } from '../services/coach/counterBiasWhisper';
import { computeBarFreshness } from '../services/barAnalysis/barFreshness';

const POLL_INTERVAL_MS = 60 * 1000;

/**
 * @param {Object} params
 * @param {Object} params.journalEntry      Journal entry for the active day. Must include `thesis`.
 * @param {string} params.symbol            Symbol whose bars to poll. e.g. 'MNQ'.
 * @param {string} params.tradingDay        Date key (YYYY-MM-DD) of the bars to fetch.
 * @param {string} params.currentTradingDay Today's trading day from the app shell.
 *                                          Whisper only runs when tradingDay === currentTradingDay.
 * @param {string} [params.recentTradeDirection]  Direction of trader's most recent
 *                                          trade today. Used by the detector to skip
 *                                          when user already entered in the opposite direction.
 * @param {boolean} [params.enabled=true]   Master kill-switch. Caller can disable
 *                                          (e.g., when a settings flag is off).
 *
 * @returns {Object}
 *   {
 *     whisper,                // Active whisper object, or null
 *     dismiss(key?: string),  // Dismiss the active whisper (or a specific key)
 *     isPolling,              // True while the polling effect is active
 *   }
 */
export const useCounterBiasWhisper = ({
  journalEntry,
  symbol,
  tradingDay,
  currentTradingDay,
  recentTradeDirection,
  enabled = true,
} = {}) => {
  const [latestWhisper, setLatestWhisper] = useState(null);
  const [freshness, setFreshness] = useState(null);
  const [isPolling, setIsPolling] = useState(false);

  // Dismissed-key set lives in a ref so dismissals don't trigger re-render
  // loops and survive across polls without thrashing useState.
  const dismissedKeysRef = useRef(new Set());

  // Reset dismiss state when we change days / symbol / thesis (a new context
  // is a clean slate — a whisper dismissed on Monday's MNQ shouldn't suppress
  // a coincidentally same-keyed Tuesday one).
  const contextKey = useMemo(() => {
    return `${tradingDay || ''}::${symbol || ''}::${journalEntry?.thesis || ''}`;
  }, [tradingDay, symbol, journalEntry?.thesis]);
  useEffect(() => {
    dismissedKeysRef.current = new Set();
    setLatestWhisper(null);
    setFreshness(null);
  }, [contextKey]);

  // Gate: do we even attempt to poll?
  const shouldPoll = Boolean(
    enabled &&
    journalEntry?.thesis &&
    typeof journalEntry.thesis === 'string' &&
    journalEntry.thesis.trim().length >= 12 &&
    symbol &&
    tradingDay &&
    currentTradingDay &&
    tradingDay === currentTradingDay
  );

  useEffect(() => {
    if (!shouldPoll) {
      setIsPolling(false);
      return undefined;
    }
    setIsPolling(true);

    let cancelled = false;

    const pollOnce = async () => {
      let bars5m = null;
      try {
        const res = await barDataService.fetchBars(
          { symbol, dateStr: tradingDay, timeframe: '5m' },
          { skipCache: false }
        );
        bars5m = res?.bars || null;
      } catch {
        // Bar fetch failures are non-fatal — silently skip this poll. The
        // detector is a "nice to have" coaching surface, not a critical path.
        return;
      }
      if (cancelled) return;
      if (!Array.isArray(bars5m) || bars5m.length === 0) {
        // Even on no-bars, surface freshness so the UI can render "feed down"
        // rather than silently rendering nothing.
        setFreshness(computeBarFreshness({ bars: bars5m || [] }));
        return;
      }

      // Compute freshness BEFORE detection so the UI can render staleness
      // even if no whisper fires this poll.
      const fresh = computeBarFreshness({ bars: bars5m });
      setFreshness(fresh);

      const whisper = detectCounterBiasSetup({
        thesis: journalEntry.thesis,
        bars5m,
        symbol,
        recentTradeDirection,
      });

      if (cancelled) return;

      if (!whisper) {
        // No actionable opposite-direction pattern this poll. We leave
        // any existing (un-dismissed) whisper visible — the user might
        // still be deciding. Only a NEW bar producing a NEW key would
        // replace it.
        return;
      }

      // If this exact whisper has already been dismissed, don't re-show.
      if (dismissedKeysRef.current.has(whisper.key)) return;

      // If the active whisper has the same key, nothing changed — skip
      // re-setting state (avoids spurious re-renders downstream).
      setLatestWhisper(prev => (prev?.key === whisper.key ? prev : whisper));
    };

    // Fire once immediately so we don't make the user wait 60s for the
    // first signal after page load.
    pollOnce();
    const id = setInterval(pollOnce, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(id);
      setIsPolling(false);
    };
    // We intentionally exclude `journalEntry` from deps (only its thesis
    // matters and that's covered by contextKey indirectly) — re-keying on
    // every journal autosave would tear down the polling effect every
    // 800ms and that's the opposite of what we want.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldPoll, symbol, tradingDay, recentTradeDirection, contextKey]);

  const dismiss = (key) => {
    const toDismiss = key || latestWhisper?.key;
    if (!toDismiss) return;
    dismissedKeysRef.current.add(toDismiss);
    setLatestWhisper(prev => (prev?.key === toDismiss ? null : prev));
  };

  return { whisper: latestWhisper, dismiss, isPolling, freshness };
};

export default useCounterBiasWhisper;
